import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Info } from "lucide-react";
import { DatasetName, DATASETS } from "@/common/constants";
import {
  isComparisonDateOperator,
  TimeCadence,
} from "@/common/report-configuration";
import {
  formatDate,
  fromLocalCalendarDate,
  isDateRangeLongerThanOneYear,
  todayUTC,
  toISODate,
  toLocalCalendarDate,
} from "@/common/dateUtils";

import {
  FakeMeasure,
  ReportDataConfiguratorAction,
  ReportDataConfiguratorState,
} from "@/components/report/designer/reportDataConfiguratorReducer";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";

import { MeasureName, MEASURES, MeasureV2 } from "@/common/measures";
import { DIMENSIONS, DimensionV2 } from "@/common/dimensions";
import { FilterDefinition, FILTERS } from "@/common/filters";

import MultiSelect from "react-select";
import type { MultiValue } from "react-select";

import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ReportDesignerState,
  ReportStateReducerActionType,
} from "@/common/reportDesignerStateReducer";
import { ParitaWorkspace } from "@/common/user";

interface ReportDataConfiguratorV2Props {
  reportDesignerState: ReportDesignerState;
  reportDesignerDispatch: React.Dispatch<ReportStateReducerActionType>;
  configuratorState: ReportDataConfiguratorState;
  configuratorDispatch: React.Dispatch<ReportDataConfiguratorAction>;
  currentWorkspace: ParitaWorkspace;
}

export const ReportDataConfiguratorV2: React.FC<ReportDataConfiguratorV2Props> =
  React.memo(
    ({
      reportDesignerState: state,
      reportDesignerDispatch: dispatch,
      configuratorState,
      configuratorDispatch,
      currentWorkspace,
    }) => {
      const [timeDimensionSelected, setTimeDimensionSelected] =
        useState<boolean>(false);
      const [comparisonRangeOpen, setComparisonRangeOpen] = useState(false);
      const [comparisonRangeDraft, setComparisonRangeDraft] = useState<
        { from: Date | undefined; to?: Date } | undefined
      >();

      // Track if a time dimension is selected
      useEffect(() => {
        const hasTimeDimension = state.reportConfig.data.dimensions.some(
          (dim) => {
            return DIMENSIONS[dim]?.isTimeDimension;
          },
        );
        setTimeDimensionSelected(hasTimeDimension);
      }, [state.reportConfig.data.dimensions]);

      const handleToggleProjections = (checked: boolean) => {
        dispatch({
          type: "toggleIncludeProjections",
          payload: checked,
        });
      };

      const handleCadenceChange = (cadence: TimeCadence) => {
        dispatch({
          type: "setTimeCadence",
          payload: cadence,
        });
      };

      const availableDatasets: { label: string; dataset: DatasetName }[] =
        Object.entries(DATASETS)
          .filter(([systemName, dataset]) => {
            return (
              currentWorkspace.dataset_access.includes(
                systemName as DatasetName,
              ) && dataset.availableToUser
            );
          })
          .map(([name, dataset]) => ({
            label: dataset.displayName,
            dataset: name as DatasetName,
          }));

      const flatAvailableMeasures = configuratorState.availableMeasures.flatMap(
        (group) => group.options,
      );
      const selectedMeasures = state.reportConfig.data.measures
        .map((measure) =>
          flatAvailableMeasures.find((m) => m.measure === measure),
        )
        .filter((m): m is MeasureV2 => m !== undefined);

      // Same for dimensions
      const flatAvailableDimensions =
        configuratorState.availableDimensions.flatMap((group) => group.options);
      const selectedDimensions = state.reportConfig.data.dimensions
        .map((dim) => flatAvailableDimensions.find((d) => d.dimension === dim))
        .filter((d): d is DimensionV2 => d !== undefined);

      // Same for filters
      const flatAvailableFilters = configuratorState.availableFilters.flatMap(
        (group) => group.options,
      );
      const selectedFilters = state.reportConfig.data.filters
        .map((filter) =>
          flatAvailableFilters.find(
            (f) => f.filter === filter.filterDefinitionName,
          ),
        )
        .filter((f): f is FilterDefinition => f !== undefined);
      const reportHasTimeDimension = state.reportConfig.data.dimensions.some(
        (dim) => DIMENSIONS[dim]?.isTimeDimension,
      );
      const dateFilters = state.reportConfig.data.filters.filter((filter) => {
        const filterDef = FILTERS[filter.filterDefinitionName];
        return filterDef.type === "date" && filterDef.supportsSinglePeriodView;
      });
      const isSimpleBarChart = ["Bar", "Horizontal Bar"].includes(
        state.reportConfig.chart.chartType,
      );
      const comparisonDateFilter =
        dateFilters.length === 1 ? dateFilters[0] : undefined;
      const comparisonDateFilterEligible =
        isSimpleBarChart &&
        !reportHasTimeDimension &&
        comparisonDateFilter !== undefined &&
        isComparisonDateOperator(comparisonDateFilter.operator);
      const previousPeriodAvailable =
        comparisonDateFilterEligible &&
        isComparisonDateOperator(comparisonDateFilter.operator);
      const oneYearAgoAvailable =
        comparisonDateFilterEligible &&
        (comparisonDateFilter.operator !== "BETWEEN" ||
          !isDateRangeLongerThanOneYear(
            comparisonDateFilter.value,
            comparisonDateFilter.secondaryValue,
          ));

      const setSelectedMeasures = (
          measures: MultiValue<MeasureV2 | FakeMeasure>,
        ) => {
          const measureNames = Array.from(measures).map((m) => m.measure);
          if (measures.length > 0) {
            // We allow both main and recruitment to be visually available by default,
            // but reportConfig has a single datasetName field.
            // If the current reportConfig dataset is common to all measures we don't touch it.
            // If not, if there is a dataset that is common to all selected measures we set it to that dataset, to ensure compatibility between measures and dataset.
            // If not, we set it to the first datasetName of the last (most recently) selected measure,
            // to ensure the measures are compatible with the dataset.
            const currentReportDataset = state.reportConfig.data.datasetName,
              currentReportDatasetCompatible = measures.every((m) =>
                MEASURES[m.measure as MeasureName].datasetNames.includes(
                  currentReportDataset,
                ),
              ),
              commonDataset = measures.reduce(
                (common, m) => {
                  const measureDatasets =
                    MEASURES[m.measure as MeasureName].datasetNames;
                  if (common === null) return measureDatasets;
                  return common.filter((dataset) =>
                    measureDatasets.includes(dataset),
                  );
                },
                null as DatasetName[] | null,
              );

            let datasetToSet: DatasetName | null = null;
            if (currentReportDatasetCompatible) {
              datasetToSet = null; // no need to change dataset
            } else if (commonDataset && commonDataset.length > 0) {
              datasetToSet = commonDataset[0]; // set to the first common dataset
            } else {
              datasetToSet =
                MEASURES[measures[measures.length - 1].measure as MeasureName]
                  .datasetNames[0]; // set to the first dataset of the last selected measure
            }

            if (
              datasetToSet !== null &&
              datasetToSet !== state.reportConfig.data.datasetName
            ) {
              dispatch({
                type: "setDataset",
                payload: datasetToSet,
              });
            }
          }
          dispatch({
            type: "setMeasures",
            payload: measureNames,
          });
        },
        setSelectedDimensions = (dimensions: MultiValue<DimensionV2>) => {
          const dimensionNames = Array.from(dimensions).map((d) => d.dimension);
          dispatch({
            type: "setDimensions",
            payload: dimensionNames,
          });
        },
        // ReportConfig.data.filters is ReportFilterV2, but the MultiSelect uses FilterDefinition
        // - setting the filters appropriately is done in the reducer
        setSelectedFilters = (filters: MultiValue<FilterDefinition>) => {
          const filterNames = Array.from(filters).map((f) => f.filter);
          dispatch({
            type: "setFiltersFromNames",
            payload: filterNames,
          });
        };

      const handleComparisonToggle = (checked: boolean) => {
        const data = state.reportConfig.data;
        let compareWith = data.compareWith;
        if (
          checked &&
          compareWith === "previousPeriod" &&
          !previousPeriodAvailable
        ) {
          compareWith = oneYearAgoAvailable ? "oneYearAgo" : "custom";
        } else if (
          checked &&
          compareWith === "oneYearAgo" &&
          !oneYearAgoAvailable
        ) {
          compareWith = "custom";
        }
        dispatch({
          type: "setComparisonSettings",
          payload: { comparisonEnabled: checked, compareWith },
        });
      };

      const renderComparisonRange = () => {
        const startValue =
          state.reportConfig.data.compareCustomPeriodStart ?? "";
        const endValue = state.reportConfig.data.compareCustomPeriodEnd ?? "";
        const selectedRange = {
          from: startValue ? toLocalCalendarDate(startValue) : undefined,
          to: endValue ? toLocalCalendarDate(endValue) : undefined,
        };
        const rangeLabel =
          startValue && endValue
            ? `${formatDate(startValue)} – ${formatDate(endValue)}`
            : startValue
              ? `${formatDate(startValue)} – Choose end date`
              : endValue
                ? `Choose start date – ${formatDate(endValue)}`
                : "Select comparison range";

        return (
          <Popover
            open={comparisonRangeOpen}
            onOpenChange={(open) => {
              if (open) {
                setComparisonRangeDraft(selectedRange);
              } else {
                setComparisonRangeDraft(undefined);
              }
              setComparisonRangeOpen(open);
            }}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-auto min-h-12 w-full justify-start gap-3 whitespace-normal px-3 py-2 text-left"
              >
                <CalendarDays className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex min-w-0 flex-col items-start">
                  <span className="text-xs text-muted-foreground">
                    Comparison period
                  </span>
                  <span className="whitespace-normal break-words">
                    {rangeLabel}
                  </span>
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-80 max-w-[calc(100vw-2rem)] p-2"
            >
              <Calendar
                mode="range"
                selected={comparisonRangeDraft ?? selectedRange}
                resetOnSelect
                onSelect={(range) => {
                  setComparisonRangeDraft(range);
                  if (!range?.from || !range.to) return;

                  dispatch({
                    type: "setComparisonSettings",
                    payload: {
                      compareCustomPeriodStart: fromLocalCalendarDate(
                        range.from,
                      ).toString(),
                      compareCustomPeriodEnd: fromLocalCalendarDate(
                        range.to,
                      ).toString(),
                    },
                  });
                  setComparisonRangeOpen(false);
                }}
              />
              <div className="flex justify-end border-t px-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setComparisonRangeDraft({ from: undefined });
                    dispatch({
                      type: "setComparisonSettings",
                      payload: {
                        compareCustomPeriodStart: undefined,
                        compareCustomPeriodEnd: undefined,
                      },
                    });
                  }}
                >
                  Clear range
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        );
      };

      const projectionsDisabled = useMemo(() => {
        return selectedMeasures.some((m) => m.disableProjections);
      }, [selectedMeasures]);

      const projectionsDisabledNote =
        "Some selected categories do not support projections.";

      return (
        <div className="space-y-4 p-4">
          {/* Category Selection */}
          <div className="space-y-2">
            <h3 className="font-semibold flex items-center">Data Sources</h3>
            <div className="space-y-2">
              {availableDatasets.map(({ label, dataset }) => (
                <div key={dataset} className="flex items-center space-x-2">
                  <Checkbox
                    id={`dataset-${dataset}`}
                    checked={configuratorState.selectedDatasets.includes(
                      dataset,
                    )}
                    onCheckedChange={(checked) => {
                      configuratorDispatch({
                        type: "toggleDataset",
                        payload: {
                          dataset,
                          checked,
                        },
                      });
                    }}
                  />
                  <Label
                    htmlFor={`dataset-${dataset}`}
                    className="text-sm cursor-pointer"
                  >
                    {label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <>
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center">Categories:</h3>
              <MultiSelect<MeasureV2 | FakeMeasure, true>
                isMulti
                value={selectedMeasures}
                onChange={setSelectedMeasures}
                options={configuratorState.availableMeasures}
              />
            </div>

            {selectedMeasures.length > 0 &&
              configuratorState.availableDimensions.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold flex items-center">
                    Dimensions:
                  </h3>

                  <MultiSelect<DimensionV2, true>
                    isMulti
                    value={selectedDimensions}
                    onChange={setSelectedDimensions}
                    options={configuratorState.availableDimensions}
                  />

                  {state.reportConfig.data.datasetName === "main" && (
                    <div className="flex items-center space-x-2 pt-2">
                      <Switch
                        id="by-date"
                        checked={state.reportConfig.data.dimensions.includes(
                          "DATE",
                        )}
                        onCheckedChange={(checked) => {
                          const currentDimensions =
                            state.reportConfig.data.dimensions;
                          const newDimensions = checked
                            ? [...currentDimensions, "DATE"]
                            : currentDimensions.filter((d) => d !== "DATE");
                          dispatch({
                            type: "setDimensions",
                            payload: newDimensions,
                          });
                        }}
                      />
                      <Label
                        htmlFor="by-date"
                        className="text-sm text-gray-600 dark:text-gray-400"
                      >
                        By Date
                      </Label>
                    </div>
                  )}

                  {timeDimensionSelected && (
                    <div className="space-y-4">
                      <div className="space-y-3 mt-2 mb-3">
                        <Select
                          value={state.reportConfig.data.timeCadence}
                          onValueChange={(value) =>
                            handleCadenceChange(value as TimeCadence)
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {["Week", "Month", "Quarter", "Year"].map(
                              (cadence) => (
                                <SelectItem key={cadence} value={cadence}>
                                  By {cadence}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="projections"
                            disabled={projectionsDisabled}
                            title={
                              projectionsDisabled ? projectionsDisabledNote : ""
                            }
                            checked={state.reportConfig.data.includeProjections}
                            onCheckedChange={handleToggleProjections}
                          />
                          <Label
                            htmlFor="projections"
                            className="text-sm text-gray-600 dark:text-gray-400"
                          >
                            Show Projected Values
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="w-[400px] p-4">
                                <h4 className="font-semibold mb-2">
                                  Projection Methodology
                                </h4>
                                <p>
                                  Projections are calculated using linear
                                  regression based on historical data points.
                                  The trend line is computed using the least
                                  squares method to find the best fit through
                                  all historical points. This line is then
                                  extended to predict future values, with
                                  appropriate constraints applied (e.g., capping
                                  certain percentage values at 100%).
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            {selectedMeasures.length > 0 &&
              configuratorState.availableFilters.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold flex items-center">Filters:</h3>
                  <MultiSelect<FilterDefinition, true>
                    isMulti
                    value={selectedFilters}
                    onChange={setSelectedFilters}
                    options={configuratorState.availableFilters}
                  />
                  {comparisonDateFilterEligible && (
                    <div className="space-y-3 border-t pt-3">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="comparison-enabled"
                          checked={state.reportConfig.data.comparisonEnabled}
                          onCheckedChange={handleComparisonToggle}
                        />
                        <Label htmlFor="comparison-enabled" className="text-sm">
                          Compare period
                        </Label>
                      </div>
                      {state.reportConfig.data.comparisonEnabled && (
                        <div className="space-y-3">
                          <Select
                            value={state.reportConfig.data.compareWith}
                            onValueChange={(value) => {
                              const data = state.reportConfig.data;
                              const today = todayUTC();
                              dispatch({
                                type: "setComparisonSettings",
                                payload: {
                                  compareWith: value,
                                  ...(value === "custom" && {
                                    compareCustomPeriodStart:
                                      data.compareCustomPeriodStart ??
                                      toISODate(today.subtract({ years: 1 })),
                                    compareCustomPeriodEnd:
                                      data.compareCustomPeriodEnd ??
                                      toISODate(today.subtract({ months: 6 })),
                                  }),
                                },
                              });
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {previousPeriodAvailable && (
                                <SelectItem value="previousPeriod">
                                  Previous period
                                </SelectItem>
                              )}
                              {oneYearAgoAvailable && (
                                <SelectItem value="oneYearAgo">
                                  Previous year
                                </SelectItem>
                              )}
                              <SelectItem value="custom">
                                Custom period
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {state.reportConfig.data.compareWith === "custom" && (
                            <div className="space-y-2">
                              {renderComparisonRange()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
          </>
        </div>
      );
    },
  );
